const DEFAULT_ALLOW_LISTS = {
  zones: [
    'ZP01',
    'ZP02'
  ],
  serviceOfferings: [
    'micro',
    'small',
    'medium',
    'large',
    'xlarge',
    '2xlarge',
    '4xlarge'
  ],
  diskOfferings: [
    'data.disk.general'
  ]
}

const networkOfferingTranslations = {
  'ISOLATED Network VXLAN with Single VR': 'Rede Isolada com VXLAN e Roteador Virtual Único',
  'ISOLATED Large Network VXLAN with Single VR': 'Rede Isolada Grande com VXLAN e Roteador Virtual Único',
  'Custom offering with Single VR': 'Oferta Personalizada com Roteador Virtual Único',
  'Custom LARGE offering with Single VR': 'Oferta Personalizada Grande com Roteador Virtual Único'
}

const vpcOfferingTranslations = {
  'Custom VPC offering with Single VR': 'Oferta de VPC Personalizada com Roteador Virtual Único',
  'Custom VPC LARGE offering with Single VR': 'Oferta de VPC Personalizada Grande com Roteador Virtual Único'
}

const WINDOWS_PATTERN = /windows/i

const isWindowsImage = (image) => WINDOWS_PATTERN.test(image.ostypename || '') || WINDOWS_PATTERN.test(image.name || '')

const isWindowsCategory = (category) => WINDOWS_PATTERN.test(category.name || '')

class AllowListService {
  constructor () {
    this.allowLists = { ...DEFAULT_ALLOW_LISTS }
  }

  setAllowList (key, items) {
    if (!Array.isArray(items)) {
      throw new Error('Items must be an array')
    }
    this.allowLists[key] = [...items]
  }

  getAllowList (key) {
    return this.allowLists[key] || []
  }

  addToAllowList (key, items) {
    if (!Array.isArray(items)) {
      throw new Error('Items must be an array')
    }
    if (!this.allowLists[key]) {
      this.allowLists[key] = []
    }
    this.allowLists[key] = [...new Set([...this.allowLists[key], ...items])]
  }

  removeFromAllowList (key, items) {
    if (!Array.isArray(items)) {
      throw new Error('Items must be an array')
    }
    if (this.allowLists[key]) {
      this.allowLists[key] = this.allowLists[key].filter(item => !items.includes(item))
    }
  }

  filterByAllowList (data, allowListKey, nameField = 'name') {
    if (!Array.isArray(data)) {
      return data
    }
    if (!(allowListKey in this.allowLists)) {
      return data
    }
    const allowedItems = this.getAllowList(allowListKey)
    if (!allowedItems.length) {
      return []
    }
    return data.filter(item => {
      const itemName = item[nameField]
      return allowedItems.includes(itemName)
    })
  }

  renameNetworkOfferings (data) {
    if (!Array.isArray(data)) {
      return data
    }
    return data.map(offering => {
      const translatedName = networkOfferingTranslations[offering.name]
      if (translatedName) {
        return {
          ...offering,
          name: translatedName,
          displaytext: translatedName
        }
      }
      return offering
    })
  }

  renameNetworkOfferingInNetworks (data) {
    if (!Array.isArray(data)) {
      return data
    }
    return data.map(network => {
      const newNetwork = { ...network }
      const translatedName = networkOfferingTranslations[newNetwork.networkofferingname]
      if (translatedName) {
        newNetwork.networkofferingname = translatedName
        newNetwork.networkofferingdisplaytext = translatedName
      }
      return newNetwork
    })
  }

  renameVpcOfferings (data) {
    if (!Array.isArray(data)) {
      return data
    }
    return data.map(offering => {
      const translatedName = vpcOfferingTranslations[offering.name]
      if (translatedName) {
        return {
          ...offering,
          name: translatedName,
          displaytext: translatedName
        }
      }
      return offering
    })
  }

  renameVpcOfferingInVpcs (data) {
    if (!Array.isArray(data)) {
      return data
    }
    return data.map(vpc => {
      const newVpc = { ...vpc }
      const translatedName = vpcOfferingTranslations[newVpc.vpcofferingname]
      if (translatedName) {
        newVpc.vpcofferingname = translatedName
      }
      return newVpc
    })
  }

  removeFromResponse (response, field, shouldRemove) {
    if (!Array.isArray(response?.[field])) {
      return
    }
    const filteredItems = response[field].filter(item => !shouldRemove(item))
    if (typeof response.count === 'number') {
      response.count -= response[field].length - filteredItems.length
    }
    response[field] = filteredItems
  }

  filterWindowsImages (data) {
    this.removeFromResponse(data.listtemplatesresponse, 'template', isWindowsImage)
    this.removeFromResponse(data.listisosresponse, 'iso', isWindowsImage)
    this.removeFromResponse(data.listoscategoriesresponse, 'oscategory', isWindowsCategory)
  }

  filterApiResponse (data) {
    if (!data) return data

    this.filterWindowsImages(data)

    if (data.listserviceofferingsresponse && data.listserviceofferingsresponse.serviceoffering) {
      const filteredOfferings = this.filterByAllowList(
        data.listserviceofferingsresponse.serviceoffering,
        'serviceOfferings'
      )
      data.listserviceofferingsresponse.serviceoffering = filteredOfferings
      data.listserviceofferingsresponse.count = filteredOfferings.length
    }

    if (data.listdiskofferingsresponse && data.listdiskofferingsresponse.diskoffering) {
      const filteredOfferings = this.filterByAllowList(
        data.listdiskofferingsresponse.diskoffering,
        'diskOfferings'
      )
      data.listdiskofferingsresponse.diskoffering = filteredOfferings
      data.listdiskofferingsresponse.count = filteredOfferings.length
    }

    if (data.listnetworkofferingsresponse && data.listnetworkofferingsresponse.networkoffering) {
      const renamedOfferings = this.renameNetworkOfferings(
        data.listnetworkofferingsresponse.networkoffering
      )
      data.listnetworkofferingsresponse.networkoffering = renamedOfferings
    }

    if (data.listnetworksresponse && data.listnetworksresponse.network) {
      const renamedNetworks = this.renameNetworkOfferingInNetworks(
        data.listnetworksresponse.network
      )
      data.listnetworksresponse.network = renamedNetworks
    }

    if (data.listvpcofferingsresponse && data.listvpcofferingsresponse.vpcoffering) {
      const renamedVpcOfferings = this.renameVpcOfferings(
        data.listvpcofferingsresponse.vpcoffering
      )
      data.listvpcofferingsresponse.vpcoffering = renamedVpcOfferings
    }

    if (data.listvpcsresponse && data.listvpcsresponse.vpc) {
      const renamedVpcs = this.renameVpcOfferingInVpcs(
        data.listvpcsresponse.vpc
      )
      data.listvpcsresponse.vpc = renamedVpcs
    }

    if (data.listzonesresponse && data.listzonesresponse.zone) {
      const filteredZones = this.filterByAllowList(
        data.listzonesresponse.zone,
        'zones',
        'name'
      )
      data.listzonesresponse.zone = filteredZones
      data.listzonesresponse.count = filteredZones.length
    }

    return data
  }

  isAllowed (allowListKey, itemName) {
    const allowedItems = this.getAllowList(allowListKey)
    return allowedItems.includes(itemName)
  }

  getAllAllowLists () {
    return { ...this.allowLists }
  }

  resetToDefaults () {
    this.allowLists = { ...DEFAULT_ALLOW_LISTS }
  }
}

const allowListService = new AllowListService()

export default allowListService
